"use client";

import { colors, fonts, spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import type { ReactElement, ReactNode } from "react";
import { SiteIdentity } from "./site-identity";
import { SiteNavigationDesktop } from "./site-navigation-desktop";
import { SiteNavigationMobile } from "./site-navigation-mobile";
import { SiteNavigationDrawer } from "./site-navigation-drawer";
import { useMobileNavigation } from "./use-mobile-navigation";
import type {
  SiteNavigationGroup,
  SiteNavigationLink,
  SiteNavigationLinkProps,
} from "./site-navigation-types";

export type SiteNavViewProps = {
  readonly homeHref: string;
  readonly brand: { readonly markSrc: string; readonly wordmarkSrc: string };
  readonly label?: string | undefined;
  readonly railPresent: boolean;
  readonly menus: readonly SiteNavigationGroup[];
  readonly doors: readonly SiteNavigationLink[];
  readonly drawerDoors: readonly SiteNavigationLink[];
  readonly action: SiteNavigationLink | null;
  readonly account?: ReactNode;
  readonly switcher?: ReactNode;
  readonly renderSearch?: ((onNavigate: () => void) => ReactNode) | undefined;
  readonly renderLink: (props: SiteNavigationLinkProps) => ReactElement;
};

export function SiteNavView({
  homeHref,
  brand,
  label,
  railPresent,
  menus,
  doors,
  drawerDoors,
  action,
  account,
  switcher,
  renderSearch,
  renderLink,
}: SiteNavViewProps) {
  const { mobileOpen, setMobileOpen, closeMobile, closeOnNavigation } =
    useMobileNavigation();
  const identity = (
    <SiteIdentity
      homeHref={homeHref}
      brand={brand}
      switcher={switcher}
      renderLink={renderLink}
      onNavigate={closeOnNavigation}
    />
  );

  return (
    <header
      {...stylex.props(styles.header, railPresent && styles.withRail)}
      data-rail-present={railPresent || undefined}
    >
      <div {...stylex.props(styles.row, railPresent && styles.railRow)}>
        {identity}
        <div {...stylex.props(styles.end)}>
          <SiteNavigationDesktop
            doors={doors}
            menus={menus}
            label={label}
            account={account}
            action={action}
            renderLink={renderLink}
            onNavigate={closeOnNavigation}
          />
          <SiteNavigationMobile
            mobileOpen={mobileOpen}
            setMobileOpen={setMobileOpen}
            identity={identity}
            account={account}
            action={action}
            renderLink={renderLink}
            onNavigate={closeOnNavigation}
          >
            <SiteNavigationDrawer
              menus={menus}
              drawerDoors={drawerDoors}
              action={action}
              account={account}
              renderSearch={renderSearch}
              renderLink={renderLink}
              onNavigate={closeOnNavigation}
              onClose={closeMobile}
            />
          </SiteNavigationMobile>
        </div>
      </div>
    </header>
  );
}

const styles = stylex.create({
  header: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    width: "100%",
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
    backgroundColor: colors.background,
    backdropFilter: "blur(8px) saturate(1.3) brightness(1.02)",
    color: colors.foreground,
    fontFamily: fonts.mono,
  },
  withRail: {
    display: { default: "block", "@media (min-width: 1024px)": "none" },
  },
  row: {
    marginInline: "auto",
    display: "flex",
    height: 64,
    width: "100%",
    maxWidth: 1200,
    alignItems: "stretch",
    boxSizing: "border-box",
    paddingInline: {
      default: 20,
      "@media (min-width: 768px)": spacing.space48,
      "@media (min-width: 1024px)": spacing.space64,
    },
  },
  railRow: { maxWidth: "none" },
  end: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flex: 1,
    minWidth: 0,
  },
});
